import { useQuery } from "@tanstack/react-query";
import type { DateTime } from "luxon";
import { useAnalytics } from "../../../contexts/analytics/hooks";
import { useChosenDepartment } from "../../../contexts/ChosenDepartment";
import { backendService } from "../../../services/backend.service";
import type { OtMode } from "../../../utils/models";
import { MC_QUERY_DEFAULTS } from "../utils";

export const useGetCostDeepDive = ({
	sinceDate,
	endDate,
	isActual,
	grouping = true,
	roles,
	jobs,
	laborCost,
	shiftHours,
	excludeHolidays,
	hideWage,
	includeLateScheduled,
	perCensus,
	forceWeekly,
	enabled,
	otMode,
	estimateMissingWage,
	departmentId: departmentIdOverride,
}: {
	sinceDate: DateTime;
	endDate?: DateTime;
	isActual: boolean;
	grouping?: boolean;
	roles?: string[];
	jobs?: string[];
	laborCost?: boolean;
	shiftHours?: string[];
	excludeHolidays?: boolean;
	hideWage?: boolean;
	includeLateScheduled?: boolean;
	/** Divide each value by that day's census → per-patient-day (PPD/HPPD) breakdown. */
	perCensus?: boolean;
	/** Force weekly grouping even on short ranges (the report's explicit Weekly toggle). Off elsewhere → auto threshold. */
	forceWeekly?: boolean;
	enabled?: boolean;
	otMode?: OtMode;
	estimateMissingWage: boolean;
	departmentId?: string;
}) => {
	const { chosenDepartment } = useChosenDepartment();
	const departmentId = departmentIdOverride || chosenDepartment.department_id;
	const { track } = useAnalytics();

	return useQuery({
		queryKey: [
			"cost-deep-dive",
			sinceDate.toFormat("yyyy-MM-dd"),
			endDate?.toFormat("yyyy-MM-dd"),
			departmentId,
			grouping,
			isActual,
			roles,
			jobs,
			laborCost,
			shiftHours,
			excludeHolidays,
			hideWage,
			includeLateScheduled,
			perCensus,
			forceWeekly,
			otMode,
			estimateMissingWage,
		],
		queryFn: async () => {
			track("fetch_cost_deep_dive", {
				department_id: departmentId,
				sinceDate: sinceDate.toFormat("yyyy-MM-dd"),
				endDate: endDate?.toFormat("yyyy-MM-dd"),
				is_actual: isActual,
				shift_hours: shiftHours?.join(","),
			});
			const { data } = await backendService.getCostDeepDive({
				departmentId,
				startDate: sinceDate,
				endDate,
				grouping,
				isActual,
				roles,
				jobs,
				laborCost,
				shiftHours,
				excludeHolidays,
				hideWage,
				includeLateScheduled,
				perCensus,
				forceWeekly,
				otMode,
				estimateMissingWage,
			});
			return data;
		},
		enabled: enabled,
		...MC_QUERY_DEFAULTS,
	});
};

export const useGetCostVariance = ({
	sinceDate,
	endDate,
	grouping = true,
	roles,
	jobs,
	laborCost,
	shiftHours,
	excludeHolidays,
	hideWage,
	includeLateScheduled,
	otMode,
	estimateMissingWage,
	// DEFAULT: matches the `enabled = true` convention of the sibling MC hooks, so pre-existing callers
	// keep fetching unchanged. Callers parameterized by persisted `mc_*` settings pass `mcSettingsReady`.
	enabled = true,
	departmentId: departmentIdOverride,
}: {
	sinceDate: DateTime;
	endDate?: DateTime;
	grouping?: boolean;
	roles?: string[];
	jobs?: string[];
	laborCost?: boolean;
	shiftHours?: string[];
	excludeHolidays?: boolean;
	hideWage?: boolean;
	includeLateScheduled?: boolean;
	otMode?: OtMode;
	estimateMissingWage: boolean;
	enabled?: boolean;
	departmentId?: string;
}) => {
	const { chosenDepartment } = useChosenDepartment();
	const departmentId = departmentIdOverride || chosenDepartment.department_id;
	const { track, trackError } = useAnalytics();
	return useQuery({
		queryKey: [
			"cost-variance",
			sinceDate.toFormat("yyyy-MM-dd"),
			endDate?.toFormat("yyyy-MM-dd"),
			departmentId,
			grouping,
			roles,
			jobs,
			laborCost,
			shiftHours,
			excludeHolidays,
			hideWage,
			includeLateScheduled,
			otMode,
			estimateMissingWage,
		],
		queryFn: async () => {
			track("fetch_cost_variance", {
				department_id: departmentId,
				sinceDate: sinceDate.toFormat("yyyy-MM-dd"),
				endDate: endDate?.toFormat("yyyy-MM-dd"),
				shift_hours: shiftHours?.join(","),
			});
			const result = await backendService.getCostVariance({
				departmentId,
				startDate: sinceDate,
				endDate,
				grouping,
				roles,
				jobs,
				laborCost,
				shiftHours,
				excludeHolidays,
				hideWage,
				includeLateScheduled,
				otMode,
				estimateMissingWage,
			});
			return result.data;
		},
		throwOnError: (error, query) => {
			trackError("fetch_cost_variance", {
				error_message: (error as Error)?.message,
				department_id: departmentId,
				sinceDate: sinceDate.toFormat("yyyy-MM-dd"),
				endDate: endDate?.toFormat("yyyy-MM-dd"),
				shift_hours: shiftHours?.join(","),
				query_key: query.queryKey.join("|"),
			});
			return false;
		},
		enabled,
		...MC_QUERY_DEFAULTS,
	});
};

export const useGetDailyCostBreakdown = ({
	date,
	endDate,
	enabled,
	isActual,
	roles,
	jobs,
	laborCost,
	shiftHours,
	hideWage,
	includeLateScheduled,
	otMode,
	estimateMissingWage,
	departmentId: departmentIdOverride,
}: {
	date: string;
	endDate?: string;
	enabled: boolean;
	isActual: boolean;
	roles?: string[];
	jobs?: string[];
	laborCost?: boolean;
	shiftHours?: string[];
	hideWage?: boolean;
	includeLateScheduled?: boolean;
	otMode?: OtMode;
	estimateMissingWage: boolean;
	departmentId?: string;
}) => {
	const { chosenDepartment } = useChosenDepartment();
	const departmentId = departmentIdOverride || chosenDepartment.department_id;
	const { track } = useAnalytics();

	return useQuery({
		queryKey: [
			"daily-cost-breakdown",
			departmentId,
			date,
			endDate,
			isActual,
			roles,
			jobs,
			shiftHours,
			laborCost,
			hideWage,
			includeLateScheduled,
			otMode,
			estimateMissingWage,
		],
		queryFn: async () => {
			track("fetch_daily_cost_breakdown", {
				department_id: chosenDepartment.department_id,
				date,
				end_date: endDate,
				is_actual: isActual,
				shift_hours: shiftHours?.join(","),
			});
			const result = await backendService.getDailyCostBreakdown({
				departmentId,
				targetDate: date,
				endDate,
				isActual,
				roles,
				jobs,
				laborCost,
				shiftHours,
				hideWage,
				includeLateScheduled,
				otMode,
				estimateMissingWage,
			});
			return result.data;
		},
		enabled,
		...MC_QUERY_DEFAULTS,
	});
};
